import React, { useEffect, useMemo, useState } from 'react';
import DataTable from 'components/DataTable';
import qs from 'query-string';
import { getPageLimit, showError, updatePageLimit } from 'utils';
import { tableStyles } from '../../../css/tableStyles';
import styles from './index.module.scss';
import ActionButton from 'components/ActionButton';
import Filter from './filters';
import CustomLink from 'components/TableLink';
import { fetchAllShoppingLists, removeShoppingList } from 'services/api/shoppingLists';
import AddShoppingListModal from 'components/AddShoppingListModal';
import DeleteActionForTable from 'components/DeleteActionForTableData';

let filters: { [p: string]: any; page: number } = {
  page: 0,
};

const ShoppingLists = props => {
  const [loading, setLoading] = useState<boolean>(false);
  const [shoppingLists, setShoppingLists] = useState<Record<string, any>>([]);
  const [addShoppingListModalOpen, setAddShoppingListModalOpen] = useState<boolean>(false);

  const toggleAddShoppingModalModal = () => setAddShoppingListModalOpen(!addShoppingListModalOpen);

  const fetchShoppingListsFn = async () => {
    const { page, ...rest } = filters;
    setLoading(true);
    try {
      const response = await fetchAllShoppingLists({
        page,
        limit: getPageLimit(),
        filter: {
          ...rest,
        },
      });
      setShoppingLists(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShoppingListsFn();
  }, []);

  const removeItem = async (itemId) => {
    await removeShoppingList(itemId);
    fetchShoppingListsFn();
  };

  const columns = useMemo(() => [
    {
      name: 'Id',
      selector: 'objectId',
      sortable: false,
      // eslint-disable-next-line react/display-name
      cell: row => (
        <CustomLink
          text={row.objectId}
          linkTo={`/shopping-lists/${row.objectId}`}
        />
      ),
    },
    {
      name: 'Name',
      selector: 'name',
      sortable: true,
      // eslint-disable-next-line react/display-name
      format: (row) => (
        <div className={styles['name']}>
          {row.name}
        </div>
      ),
    },
    {
      name: 'No. of items',
      selector: 'items',
      sortable: true,
      format: (row) => (
        <div className={styles['name']}>
          {row.items?.length || 0}
        </div>
      ),
    },
    {
      name: 'Push to cart count',
      selector: 'items',
      sortable: true,
      format: (row) => (
        <div className={styles['name']}>
          {row.pushToCartCount || 0}
        </div>
      ),
    },
    {
      name: 'Purchase count',
      selector: 'items',
      sortable: true,
      format: (row) => (
        <div className={styles['name']}>
          {row.purchaseCount || 0}
        </div>
      ),
    },
    {
      name: 'Public',
      selector: 'isPublic',
      sortable: true,
      format: (row) => row.isPublic?.toString() || 'false',
    },
    {
      name: 'Featured',
      selector: 'isFeatured',
      sortable: true,
      format: (row) => row.isFeatured?.toString() || 'false',
    },
    {
      name: 'Action',
      selector: '',
      sortable: true,
      // eslint-disable-next-line react/display-name
      cell: (row: Record<string, any>) => (
        <div className={'ml-3'}>
          <DeleteActionForTable
            onClick={() => {
              removeItem(row?.objectId);
            }}
          />
        </div>
      ),
    },
  ], []);


  const onFilterSubmit = (updatedFilter: Record<string, any>) => {
    filters = { ...updatedFilter, page: 0 };
    updateQueryString();
    fetchShoppingListsFn();
  };

  const filterToQueryString = () => {
    return qs.stringify({ ...filters });
  };

  const updateQueryString = () => {
    const qs = filterToQueryString();
    props.history.push({
      pathname: props.location.pathname,
      search: qs,
    });
  };

  const onChangePage = (page: number) => {
    filters.page = page - 1;
    updateQueryString();
    fetchShoppingListsFn();
  };

  const onChangeRowsPerPage = (rowsPerPage: number, currentPage: number) => {
    filters.limit = rowsPerPage;
    updatePageLimit(rowsPerPage);
    onChangePage(currentPage);
  };

  return (
    <div>
      <div className={styles['main-container']}>
        <div className={styles['header']}>
          <div className={styles['heading']}>Shopping Lists</div>
          <div className={styles['buttons-container']}>
            <ActionButton
              text={<>Shopping List <span>+</span></>}
              active={true}
              isLoading={false}
              onClick={() => { setAddShoppingListModalOpen(true) }}
              customClass={styles['add-new-pick-pack-button']}
            />
          </div>
        </div>
        {addShoppingListModalOpen && <AddShoppingListModal
          toggleModal={toggleAddShoppingModalModal}
          onSuccess={() => { fetchShoppingListsFn() }}
        />}
        <Filter {...props} onSubmit={onFilterSubmit} />
        <div style={{ padding: '20px 15px' }}>
          <DataTable
            keyField="objectId"
            columns={columns}
            noHeader={true}
            data={shoppingLists?.items}
            highlightOnHover={true}
            customStyles={tableStyles}
            pagination={true}
            paginationServer={true}
            paginationPerPage={getPageLimit()}
            paginationDefaultPage={(filters.page || 0) + 1}
            paginationTotalRows={shoppingLists.total}
            paginationRowsPerPageOptions={[50, 100, 200]}
            onChangePage={onChangePage}
            onChangeRowsPerPage={onChangeRowsPerPage}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
};

export { ShoppingLists };
